/**
 * Single-key page shortcuts (`e` for edit, `n` for new, …).
 *
 * A page binds a map of keys to actions for as long as it is mounted and
 * unbinds it from `dispose`, so shortcuts never outlive the page that meant
 * them. Keys are matched case-insensitively, which makes `e` and `E` the same
 * shortcut without listing both — and means Shift is the one modifier that
 * does *not* disqualify a press.
 *
 * The listener stays out of the way of typing: a press that reaches an
 * input, textarea, select or contenteditable is theirs, and any press with
 * Ctrl, Alt or Cmd held is the browser's (Cmd-N is a new window, not a new
 * note). A handler that already called `preventDefault` — the rich editor,
 * a dialog — also wins.
 */

export type PageKeyMap = Readonly<Record<string, () => void>>;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) {
    return true;
  }
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/** Binds `keys` on the document and returns the function that unbinds them. */
export function bindPageKeys(keys: PageKeyMap): () => void {
  const byKey = new Map<string, () => void>();
  for (const [key, action] of Object.entries(keys)) {
    byKey.set(key.toLowerCase(), action);
  }

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.defaultPrevented || event.ctrlKey || event.altKey || event.metaKey || event.isComposing) {
      return;
    }
    if (isTypingTarget(event.target)) {
      return;
    }
    const action = byKey.get(event.key.toLowerCase());
    if (action === undefined) {
      return;
    }
    event.preventDefault();
    action();
  };

  document.addEventListener("keydown", onKeyDown);
  return (): void => document.removeEventListener("keydown", onKeyDown);
}
